import { fuseBvp, heartRate, hrv } from "@musejs/bio";
import { MuseError, SignalQualityMonitor, type SignalChunk } from "@musejs/core";
import { ArtifactMonitor, BandPowerMonitor } from "@musejs/features";
import { connect, type MuseDevice } from "@musejs/muse";
import { readSession, replayTransport } from "@musejs/recording";
import { WebBluetoothTransport, webBluetoothTransport } from "@musejs/web";
import { dominantFrequency, type Dominant } from "@musejs/signal";
import { ChannelBuffer, rms } from "./analysis.js";
import { inspectSession } from "./inspect.js";
import { Scope } from "./scope.js";
import {
  el,
  renderArtifacts,
  renderBandLegend,
  renderBandMatrix,
  renderDevice,
  renderDominants,
  renderInspect,
  renderInspectError,
  renderQuality,
} from "./views.js";

const SCALP = ["TP9", "AF7", "AF8", "TP10"] as const;

/** Most recent value in a column. */
const last = (values: Float32Array): number => values[values.length - 1] as number;
const ACCEL = ["accX", "accY", "accZ"] as const;
const GYRO = ["gyroX", "gyroY", "gyroZ"] as const;

// ── Instruments ─────────────────────────────────────────────────────────────

const scopes = {
  eeg: new Scope(el<HTMLCanvasElement>("eegCanvas"), {
    windowSeconds: 4,
    channels: SCALP,
    range: "auto",
  }),
  optics: new Scope(el<HTMLCanvasElement>("opticsCanvas"), {
    windowSeconds: 8,
    channels: ["LI_NIR", "RI_NIR", "LI_IR", "RI_IR"],
    range: "auto",
    removeDc: true,
  }),
  drlref: new Scope(el<HTMLCanvasElement>("drlCanvas"), {
    windowSeconds: 8,
    channels: ["DRL", "REF"],
    range: "auto",
    removeDc: true,
  }),
  accelerometer: new Scope(el<HTMLCanvasElement>("accCanvas"), {
    windowSeconds: 10,
    channels: ACCEL,
    // Fixed at ±2 g, the sensor's own range: auto-scaling a still headband
    // would magnify noise into something that looks like movement.
    range: 2,
  }),
  gyroscope: new Scope(el<HTMLCanvasElement>("gyroCanvas"), {
    windowSeconds: 10,
    channels: GYRO,
    range: 250,
  }),
};

const quality = new SignalQualityMonitor();
const bands = new BandPowerMonitor({ windowSeconds: 2 });
const artifacts = new ArtifactMonitor();
const eegBuffer = new ChannelBuffer(4, 256);
const opticsBuffer = new ChannelBuffer(20, 64);
const imuBuffer = new ChannelBuffer(4, 52);
/** Heart rate over time, for the trend line. */
const bpmTrend: number[] = [];

/**
 * How often text refreshes, as opposed to traces. Canvas traces redraw every
 * frame; a number that changes sixty times a second cannot be read, and an
 * analysis over a window several seconds long learns nothing new in one frame.
 * [M] Recomputed every frame, the Frequency cards flipped between "none" and a
 * peak 1.6 times a second, and the heart-rate trend's 240 points spanned four
 * seconds instead of four minutes.
 */
const READOUT_MS = 250;
const ANALYSIS_MS = 1000;

/** Due at most once per `ms`; after `reset()` the next check is due at once. */
function cadence(ms: number): { due(now: number): boolean; reset(): void } {
  let previous = Number.NEGATIVE_INFINITY;
  return {
    due(now) {
      if (now - previous < ms) return false;
      previous = now;
      return true;
    },
    reset() {
      previous = Number.NEGATIVE_INFINITY;
    },
  };
}
const readouts = cadence(READOUT_MS);
const analyses = cadence(ANALYSIS_MS);

/** The latest finite timing uncertainty, in ms; rendered at the readout cadence. */
let uncertaintyMs: number | null = null;

let device: MuseDevice | null = null;
let recording: ReturnType<MuseDevice["record"]> | null = null;
let view = "chart";

// ── Chrome ──────────────────────────────────────────────────────────────────

const statusValue = el("statusValue");
const notice = el("notice");
const connectButton = el<HTMLButtonElement>("connectButton");
const replayButton = el<HTMLButtonElement>("replayButton");
const recordButton = el<HTMLButtonElement>("recordButton");
const presetSelect = el<HTMLSelectElement>("presetSelect");
const uncertaintyText = el("uncertaintyText");
const uncertaintyIdle = uncertaintyText.innerHTML;
const uncertaintyBar = el("uncertaintyBar");

function say(message: string | null): void {
  if (message === null) {
    notice.hidden = true;
    return;
  }
  notice.textContent = message;
  notice.hidden = false;
}

function setStatus(text: string, live = false): void {
  statusValue.textContent = text;
  statusValue.dataset["live"] = String(live);
}

function showView(next: string): void {
  view = next;
  readouts.reset();
  analyses.reset();
  for (const section of document.querySelectorAll<HTMLElement>(".view")) {
    section.hidden = section.dataset["view"] !== next;
  }
  for (const key of document.querySelectorAll<HTMLElement>(".dial__key")) {
    const active = key.dataset["view"] === next;
    key.setAttribute("aria-current", String(active));
  }
}

// ── Connection ──────────────────────────────────────────────────────────────

async function onConnect(): Promise<void> {
  if (device !== null) {
    await teardown();
    return;
  }
  if (!WebBluetoothTransport.supported) {
    say(
      "This browser has no Web Bluetooth. Firefox and Safari have both declined to implement " +
        "it, which includes every browser on iOS. Use the Node transport or the CLI.",
    );
    return;
  }
  // Asked before the chooser is attempted: when the browser itself has no
  // Bluetooth access, `requestDevice` produces no picker and one console line,
  // which looks like a broken page rather than a permission.
  if ((await WebBluetoothTransport.available()) === false) {
    say(
      "The browser reports no usable Bluetooth adapter. Either it is switched off, or the " +
        "browser has not been granted Bluetooth access — on macOS, System Settings → " +
        "Privacy & Security → Bluetooth, then restart the browser.",
    );
    return;
  }

  say(null);
  connectButton.disabled = true;
  setStatus("connecting");

  try {
    device = await connect(webBluetoothTransport());
    attachStreams(device);
    connectButton.textContent = "Disconnect";
    recordButton.disabled = false;
    replayButton.disabled = true;

    const preset = presetSelect.value;
    await device.start({ preset });
    el("presetValue").textContent = preset;
    setStatus("streaming", true);
    // Fire and forget: the answer arrives on the Device view whenever it does.
    void device.deviceInfo().catch(() => undefined);
  } catch (error) {
    await teardown();
    say(error instanceof Error ? error.message : String(error));
  } finally {
    connectButton.disabled = device === null && !WebBluetoothTransport.supported;
  }
}

async function onReplay(): Promise<void> {
  if (device !== null) {
    await teardown();
    return;
  }
  say(null);
  replayButton.disabled = true;
  setStatus("loading capture");

  try {
    const response = await fetch(`${import.meta.env.BASE_URL}fixtures/p1041-pulse.musez`);
    if (!response.ok) throw new Error(`Capture unavailable (HTTP ${response.status})`);
    const session = readSession(new Uint8Array(await response.arrayBuffer()), {
      requireIntegrity: true,
    });

    device = await connect(replayTransport(session, { speed: "realtime" }), { handshake: false });
    attachStreams(device);
    el("presetValue").textContent = `${session.manifest.preset.requested ?? "p1041"} (replay)`;
    setStatus("replaying", true);
    connectButton.disabled = true;
    replayButton.textContent = "Stop replay";
    replayButton.disabled = false;
    replayButton.dataset["armed"] = "true";
    say(
      "Replaying a 60-second capture recorded on a worn headband. Every view is fed by the " +
        "same code a live connection uses.",
    );
  } catch (error) {
    await teardown();
    say(error instanceof Error ? error.message : String(error));
  }
}

function attachStreams(target: MuseDevice): void {
  // Chunks keep arriving while a torn-down device stops and disconnects. Drawn,
  // they put a sliver of trace, fresh readouts and a contact report back on a
  // page that had just been cleared, [M] as seen live in Chrome.
  const live = (): boolean => target === device;
  target.on("eeg", (chunk) => {
    if (!live()) return;
    scopes.eeg.accept(chunk);
    quality.accept(chunk);
    bands.accept(chunk);
    artifacts.accept(chunk);
    eegBuffer.push(chunk.channels, chunk.channelNames, chunk.sampleRate);
    noteTiming(chunk);
  });
  target.on("optics", (chunk) => {
    if (!live()) return;
    scopes.optics.accept(chunk);
    opticsBuffer.push(chunk.channels, chunk.channelNames, chunk.sampleRate);
    noteTiming(chunk);
  });
  target.on("imu", (chunk) => {
    if (!live()) return;
    scopes.accelerometer.accept(chunk);
    scopes.gyroscope.accept(chunk);
    imuBuffer.push(chunk.channels, chunk.channelNames, chunk.sampleRate);
  });
  target.on("drlref", (chunk) => {
    if (live()) scopes.drlref.accept(chunk);
  });
  target.on("battery", (chunk) => {
    if (!live()) return;
    const percent = chunk.channels["percent"]?.[0];
    if (percent !== undefined) el("batteryValue").textContent = `${percent.toFixed(0)}%`;
  });
}

function noteTiming(chunk: SignalChunk): void {
  // NaN until the clock has a fit: say nothing rather than "±NaN ms".
  if (Number.isFinite(chunk.timing.uncertaintyUs)) uncertaintyMs = chunk.timing.uncertaintyUs / 1000;
}

function renderTiming(): void {
  if (uncertaintyMs === null) return;
  const ms = uncertaintyMs;
  uncertaintyText.innerHTML =
    `Timestamps carry <b>±${ms.toFixed(0)} ms</b>, interpolated from the nominal rate. The ` +
    `shaded margin on every trace is that figure drawn to scale: it is how far a sample could ` +
    `sit from where the plot puts it. The limit is Bluetooth buffering, not the headband's crystal.`;
  uncertaintyBar.style.setProperty("--span", `${Math.max(2, (ms / 4000) * 132)}px`);
  uncertaintyBar.hidden = false;
  el("mtuValue").textContent = device === null || device.mtu === null ? "not yet known" : `${device.mtu} B`;
}

async function teardown(): Promise<void> {
  recording = null;
  recordButton.dataset["armed"] = "false";
  recordButton.textContent = "Record";
  recordButton.disabled = true;
  connectButton.textContent = "Connect";
  connectButton.disabled = !WebBluetoothTransport.supported;
  replayButton.textContent = "Replay a capture";
  replayButton.dataset["armed"] = "false";
  replayButton.disabled = false;
  el("presetValue").textContent = "—";
  el("batteryValue").textContent = "—";
  el("mtuValue").textContent = "—";
  uncertaintyBar.hidden = true;
  uncertaintyText.innerHTML = uncertaintyIdle;
  uncertaintyMs = null;
  setStatus("not connected");

  for (const scope of Object.values(scopes)) scope.clear();
  for (const buffer of [eegBuffer, opticsBuffer, imuBuffer]) buffer.clear();
  bpmTrend.length = 0;
  quality.reset();
  bands.reset();
  artifacts.reset();

  const current = device;
  device = null;
  if (current !== null) {
    try {
      await current.stop();
      await current.disconnect();
    } catch {
      // A link that has already dropped cannot be closed politely, and saying
      // so on the way out would only bury the message that mattered.
    }
  }
}

/**
 * Notices a session that ended on its own — a replay reaching the end of its
 * capture, a link that dropped and could not be re-established, a transport
 * refusing the link — and says why, rather than leaving a frozen trace that
 * still reads "streaming".
 */
function noticeEnd(): void {
  if (device === null) return;
  const status = device.status;
  if (status !== "disconnected" && status !== "error") return;

  const failure = device.failure;
  const replaying = replayButton.dataset["armed"] === "true";
  // A recording in progress is kept, not discarded with the session.
  if (recording !== null) onRecord();
  const message =
    failure !== null
      ? failure.message
      : replaying
        ? "The replay reached the end of the capture."
        : status === "error"
          ? "The link dropped and could not be re-established."
          : "The headband disconnected.";
  void teardown().then(() => say(message));
}

// ── Recording ───────────────────────────────────────────────────────────────

function onRecord(): void {
  if (device === null) return;
  if (recording === null) {
    recording = device.record({ task: "playground" });
    recordButton.dataset["armed"] = "true";
    recordButton.textContent = "Stop";
    return;
  }

  const bytes = recording.finish();
  recording = null;
  recordButton.dataset["armed"] = "false";
  recordButton.textContent = "Record";

  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "application/zip" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `muse-${new Date().toISOString().replaceAll(/[:.]/g, "-")}.musez`;
  anchor.click();
  URL.revokeObjectURL(url);
}

// ── Inspect ─────────────────────────────────────────────────────────────────

async function inspectFile(file: File): Promise<void> {
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    renderInspect(inspectSession(file.name, bytes));
  } catch (error) {
    renderInspectError(
      MuseError.is(error)
        ? `[${error.code}] ${error.message}`
        : error instanceof Error
          ? error.message
          : String(error),
    );
  }
}

function wireDropZone(): void {
  const zone = el("dropZone");
  const input = el<HTMLInputElement>("fileInput");

  zone.addEventListener("click", () => input.click());
  zone.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      input.click();
    }
  });
  input.addEventListener("change", () => {
    const file = input.files?.[0];
    if (file !== undefined) void inspectFile(file);
  });

  for (const type of ["dragenter", "dragover"]) {
    zone.addEventListener(type, (event) => {
      event.preventDefault();
      zone.dataset["over"] = "true";
    });
  }
  for (const type of ["dragleave", "drop"]) {
    zone.addEventListener(type, () => {
      zone.dataset["over"] = "false";
    });
  }
  zone.addEventListener("drop", (event) => {
    event.preventDefault();
    const file = (event as DragEvent).dataTransfer?.files?.[0];
    if (file !== undefined) void inspectFile(file);
  });
}

// ── Per-view rendering ──────────────────────────────────────────────────────

function renderPens(listId: string, scope: Scope, unit: string, digits = 1): void {
  const latest = scope.latest();
  el(listId).replaceChildren(
    ...[...latest].map(([name, value]) => {
      const li = document.createElement("li");
      li.textContent = `${name} ${value.toFixed(digits)}${unit}`;
      return li;
    }),
  );
}

function renderMotion(): void {
  renderPens("accPens", scopes.accelerometer, " g", 2);
  renderPens("gyroPens", scopes.gyroscope, "°/s", 0);

  const x = imuBuffer.get("accX");
  const y = imuBuffer.get("accY");
  const z = imuBuffer.get("accZ");
  if (x === null || y === null || z === null || x.length === 0) return;

  const magnitude = Math.hypot(last(x), last(y), last(z));
  el("accMagnitude").textContent = `${magnitude.toFixed(3)} g`;
  el("accX").textContent = `${last(x).toFixed(3)} g`;
  el("accY").textContent = `${last(y).toFixed(3)} g`;
  el("accZ").textContent = `${last(z).toFixed(3)} g`;

  // Rotation rate is the better stillness cue: gravity dominates the
  // accelerometer whatever the head is doing.
  const gyroX = imuBuffer.get("gyroX");
  const gyroY = imuBuffer.get("gyroY");
  const gyroZ = imuBuffer.get("gyroZ");
  if (gyroX === null || gyroY === null || gyroZ === null) return;
  const spin = Math.hypot(rms(gyroX), rms(gyroY), rms(gyroZ));
  const still = spin < 5;
  const node = el("stillValue");
  node.textContent = still ? "yes" : `no · ${spin.toFixed(0)}°/s`;
  node.dataset["flag"] = String(!still);
}

function renderFrequency(): void {
  const entries = new Map<string, Dominant | null>();
  for (const name of SCALP) {
    const signal = eegBuffer.get(name);
    entries.set(
      name,
      signal === null ? null : dominantFrequency(signal, eegBuffer.sampleRate),
    );
  }
  renderDominants(entries);
}

function renderHeart(): void {
  const rate = opticsBuffer.sampleRate;
  // pulseSqi declines under 12 s, and fuseBvp would then weight every optode alike.
  if (rate === 0 || opticsBuffer.length < rate * 12) return;

  const { bvp, sqi } = fuseBvp(opticsBuffer.snapshot(), { sampleRate: rate });
  const beat = heartRate(bvp, { sampleRate: rate });
  const variability = hrv(beat.peakTimes);

  el("bpmValue").textContent = beat.bpm === null ? "—" : beat.bpm.toFixed(0);
  el("hrConfidence").textContent = beat.bpm === null ? "—" : beat.confidence.toFixed(2);
  el("hrBeats").textContent = String(beat.peaks.length);
  el("rrValue").textContent = variability === null ? "—" : `${variability.meanRrMs.toFixed(0)} ms`;
  el("sdnnValue").textContent = variability === null ? "—" : `${variability.sdnnMs.toFixed(0)} ms`;
  el("rmssdValue").textContent = variability === null ? "—" : `${variability.rmssdMs.toFixed(0)} ms`;

  const best = Object.entries(sqi).toSorted((a, b) => b[1] - a[1])[0];
  el("bestOptode").textContent =
    best === undefined ? "—" : `${best[0]} · ${best[1].toFixed(2)}`;

  if (beat.bpm !== null && beat.confidence > 0.5) {
    bpmTrend.push(beat.bpm);
    if (bpmTrend.length > 240) bpmTrend.shift();
  }
  drawTrend();
}

function drawTrend(): void {
  const canvas = el<HTMLCanvasElement>("trendCanvas");
  const context = canvas.getContext("2d");
  if (context === null) return;

  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  const rect = canvas.getBoundingClientRect();
  canvas.width = Math.max(1, Math.round(rect.width * ratio));
  canvas.height = Math.max(1, Math.round(rect.height * ratio));
  context.clearRect(0, 0, canvas.width, canvas.height);

  el("trendEmpty").hidden = bpmTrend.length >= 2;
  if (bpmTrend.length < 2) return;

  const style = getComputedStyle(document.documentElement);
  // A fixed 40–160 bpm axis rather than one fitted to the data: an axis that
  // rescales itself makes a steady pulse look like it is swinging wildly.
  const low = 40;
  const high = 160;
  const toY = (bpm: number): number =>
    canvas.height - ((Math.max(low, Math.min(high, bpm)) - low) / (high - low)) * canvas.height;

  context.strokeStyle = style.getPropertyValue("--grid-major").trim();
  context.lineWidth = ratio;
  for (const line of [60, 80, 100, 120]) {
    const y = Math.round(toY(line)) + 0.5;
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(canvas.width, y);
    context.stroke();
  }

  context.strokeStyle = style.getPropertyValue("--flag").trim();
  context.lineWidth = 2 * ratio;
  context.lineJoin = "round";
  context.beginPath();
  const step = canvas.width / Math.max(1, bpmTrend.length - 1);
  for (const [index, bpm] of bpmTrend.entries()) {
    const x = index * step;
    const y = toY(bpm);
    if (index === 0) context.moveTo(x, y);
    else context.lineTo(x, y);
  }
  context.stroke();
}

// ── Frame loop ──────────────────────────────────────────────────────────────

function frame(now: number): void {
  noticeEnd();
  // Traces every frame; text and analyses on their own cadence — see READOUT_MS.
  const readout = readouts.due(now);
  const analysis = analyses.due(now);
  if (readout) renderTiming();
  if (view === "chart") {
    for (const scope of [scopes.eeg, scopes.optics, scopes.drlref]) scope.draw();
    el("eegEmpty").hidden = scopes.eeg.latest().size > 0;
    el("opticsEmpty").hidden = scopes.optics.latest().size > 0;
    el("drlEmpty").hidden = scopes.drlref.latest().size > 0;
    if (readout) {
      renderPens("eegPens", scopes.eeg, " µV");
      renderPens("opticsPens", scopes.optics, "", 3);
      renderPens("drlPens", scopes.drlref, " µV");
      renderQuality(quality.report(), SCALP);
      const report = artifacts.report();
      renderArtifacts(report.blink, report.jawClench);
    }
  } else if (view === "motion") {
    scopes.accelerometer.draw();
    scopes.gyroscope.draw();
    el("accEmpty").hidden = scopes.accelerometer.latest().size > 0;
    el("gyroEmpty").hidden = scopes.gyroscope.latest().size > 0;
    if (readout) renderMotion();
  } else if (view === "bands") {
    if (readout) renderBandMatrix(bands.report(), SCALP);
  } else if (view === "frequency") {
    if (analysis) renderFrequency();
  } else if (view === "heart") {
    if (analysis) renderHeart();
  } else if (view === "device") {
    if (readout && device !== null) renderDevice(device.info, device.mtu, device.protocol);
  }

  requestAnimationFrame(frame);
}

// ── Wiring ──────────────────────────────────────────────────────────────────

connectButton.addEventListener("click", () => void onConnect());
replayButton.addEventListener("click", () => void onReplay());
recordButton.addEventListener("click", onRecord);
presetSelect.addEventListener("change", () => {
  if (device !== null) {
    say("The preset applies on the next connection. Disconnect and reconnect to change it.");
  }
});
for (const key of document.querySelectorAll<HTMLElement>(".dial__key")) {
  key.addEventListener("click", () => showView(key.dataset["view"] as string));
}

wireDropZone();
renderBandLegend();
showView("chart");

if (!WebBluetoothTransport.supported) {
  say(
    "Web Bluetooth is not available here. Chromium browsers support it; Firefox and Safari " +
      "have both declined, and every browser on iOS uses WebKit. Replay still works.",
  );
  connectButton.disabled = true;
}

requestAnimationFrame(frame);
